import axios, { type AxiosRequestConfig } from "axios";

import { type UserResponse } from "@north-beam/nb-common";

import {
  type ApiEnvironment,
  type HeadersParams,
  type UserConfig,
} from "./types";
import { type AbstractUser, type UserClass } from "./user";
import { makeHeaders } from "./util";

export class UserManager<U extends AbstractUser<C>, C extends UserConfig> {
  private readonly env: ApiEnvironment;
  private readonly model: UserClass<U, C>;

  constructor(env: ApiEnvironment, model: UserClass<U, C>) {
    this.env = env;
    this.model = model;
  }

  private async callApi<T>(
    url: string,
    {
      impersonateUserId,
      getTokenSilently,
      isDemoMode,
      useDevelopmentAnalyticsData,
      headers: _headers,
      actingUserId,
      ...config
    }: HeadersParams & Omit<AxiosRequestConfig, "url">,
  ) {
    const result = await this.axios.request<T>({
      ...config,
      url,
      headers: {
        ..._headers,
        ...(await makeHeaders({
          impersonateUserId,
          getTokenSilently,
          isDemoMode,
          useDevelopmentAnalyticsData,
          actingUserId,
        })),
      },
    });
    return result.data;
  }

  private async saveAndFetch(
    email: string,
    params: HeadersParams,
    actingUserId?: string,
  ): Promise<UserResponse> {
    return this.callApi("/api/user", {
      ...params,
      actingUserId,
      method: "POST",
      data: {
        email,
        impersonateUserId: params.impersonateUserId,
        actingUserId,
      },
    });
  }

  get axios() {
    return axios.create({
      baseURL: this.env.apiRoot,
      withCredentials: true,
    });
  }

  async create(
    email: string,
    params: Omit<C, "email" | "authedUserResponse" | "env">,
    actingUserId?: string,
  ): Promise<U | null> {
    let response: UserResponse;
    try {
      response = await this.saveAndFetch(email, params, actingUserId);
    } catch (e) {
      return null;
    }
    return new this.model({
      email,
      authedUserResponse: response,
      env: this.env,
      actingUserId,

      ...params,
    } as C);
  }
}
